import { ApplicationViewPersistence } from './application-view.persistence';
import type { ApplicationIndex } from '@/core/conversations/conversation-index/conversation-index.type';
import { ErrorService } from '@/core/shared/errors/error.service';
import { ref } from 'vue';
import dayjs from 'dayjs';

export default class ApplicationViewService {
  // Feature release date - applications before this are always viewed
  private static readonly FEATURE_RELEASE_DATE = new Date('2025-07-29T00:00:00Z');

  private readonly persistence = new ApplicationViewPersistence();
  // Local cache for optimistic updates - tracks viewed application IDs
  private viewedApplicationIds = ref<Set<number>>(new Set());

  /**
   * Determines if an application should be shown as viewed
   * Logic:
   * - If created before FEATURE_RELEASE_DATE: always viewed
   * - If created after and has watcher record: use record status
   * - If created after and no watcher record: unviewed
   */
  public isApplicationViewed(application: ApplicationIndex): boolean {
    // Check local cache first
    if (this.viewedApplicationIds.value.has(application.applicationId)) {
      return true;
    }

    // Parse application creation date
    const applicationDate = dayjs(application.createTs);

    // Old applications are always viewed
    if (applicationDate.isBefore(ApplicationViewService.FEATURE_RELEASE_DATE)) {
      return true;
    }

    // For new applications, check watcher record from backend
    if (application.hasViewedRecord) {
      return application.isViewed ?? false;
    }

    // No record = unviewed for new applications
    return false;
  }

  /**
   * Marks an application as viewed by current user
   * Uses optimistic update pattern for immediate UI feedback
   */
  public async markApplicationAsViewed(applicationId: number): Promise<void> {
    // Optimistic update - add to local cache immediately
    this.viewedApplicationIds.value.add(applicationId);

    try {
      await this.persistence.markAsViewed(applicationId);
    } catch (error) {
      this.viewedApplicationIds.value.delete(applicationId);

      ErrorService.captureException(error);
    }
  }

  /**
   * Clears the local viewed applications cache
   * Called when user navigates away or logs out
   */
  public clearViewedCache(): void {
    this.viewedApplicationIds.value.clear();
  }
}
